// src/components/superadmin/TicketsList.jsx
// #Revised UI_v2 — SE5: severity colour badges, SLA progress bar, quick-reply template dropdown
import React, { useState, useEffect } from 'react';
import { getAllTickets, getTicketById, updateTicket, addTicketMessage } from '../../api/ticketService';
import '../../styles/tickets.css';

const STATUS_OPTIONS   = ['Waiting for Support', 'Waiting for Customer', 'Closed'];
const SEVERITY_OPTIONS = ['1 - System Down', '2 - Significant Impact', '3 - Minor Impact', '4 - Informational Only'];
const TYPE_OPTIONS     = ['Technical Issues', 'Billing and Payments', 'Product Inquiries', 'Feedback', 'Others'];

// #Revised UI_v2 — SE5: start — SLA hours per severity
const SLA_HOURS = {
  '1 - System Down':         4,
  '2 - Significant Impact':  8,
  '3 - Minor Impact':        24,
  '4 - Informational Only':  72,
};

// #Revised UI_v2 — SE5: severity label + CSS class mapping
const SEVERITY_META = {
  '1 - System Down':        { label: 'Critical', cls: 'sev-critical' },
  '2 - Significant Impact': { label: 'High',     cls: 'sev-high'     },
  '3 - Minor Impact':       { label: 'Medium',   cls: 'sev-medium'   },
  '4 - Informational Only': { label: 'Low',      cls: 'sev-low'      },
};

// #Revised UI_v2 — SE5: quick-reply templates
const REPLY_TEMPLATES = [
  { label: 'Select a template…', value: '' },
  { label: 'Acknowledgement',    value: 'Thank you for contacting BespokeBeauty Support. We have received your ticket and will investigate shortly.' },
  { label: 'Under investigation',value: 'We are currently investigating this issue and will provide an update as soon as possible.' },
  { label: 'Request more info',  value: 'Could you please provide more details about the issue you are experiencing? Screenshots or step-by-step reproduction steps would be very helpful.' },
  { label: 'Escalated',          value: 'This ticket has been escalated to our technical team for further investigation. We will keep you updated on progress.' },
  { label: 'Resolved',           value: 'This issue has been resolved. Please let us know if you continue to experience any problems and we will be happy to assist further.' },
];

function getSlaProgress(ticket) {
  if (ticket.ticketStatus === 'Closed') return null;
  const slaHours = SLA_HOURS[ticket.ticketSeverity];
  if (!slaHours) return null;
  const created  = ticket.dateCreated?.seconds
    ? new Date(ticket.dateCreated.seconds * 1000)
    : new Date(ticket.dateCreated);
  const elapsed  = (Date.now() - created.getTime()) / 3600000; // hours elapsed
  const pct      = Math.min(Math.round((elapsed / slaHours) * 100), 100);
  const overdue  = elapsed > slaHours;
  const color    = overdue || pct >= 90 ? '#EF4444'
                 : pct >= 50           ? '#F59E0B'
                 :                       '#22C55E';
  const remaining = Math.max(slaHours - elapsed, 0);
  const label     = overdue
    ? `SLA breached by ${Math.round(elapsed - slaHours)}h`
    : `${Math.round(remaining)}h remaining`;
  return { pct, color, label, overdue };
}
// #Revised UI_v2 — SE5: end

function TicketsList() {
  const [tickets,       setTickets]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedTicket,setSelectedTicket]= useState(null);
  const [replyText,     setReplyText]     = useState('');
  const [sending,       setSending]       = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [editFields,    setEditFields]    = useState({});
  // #Revised UI_v2 — SE5: template dropdown state
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // Filters
  const [filterSalonId, setFilterSalonId] = useState('');
  const [filterStatus,  setFilterStatus]  = useState('');
  const [filterType,    setFilterType]    = useState('');

  useEffect(() => { loadTickets(); }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await getAllTickets();
      setTickets(data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const ticket = await getTicketById(ticketId);
      setSelectedTicket(ticket);
      setEditFields({
        ticketStatus:   ticket.ticketStatus,
        ticketSeverity: ticket.ticketSeverity,
        ticketType:     ticket.ticketType,
      });
      setReplyText('');
      setSelectedTemplate('');
    } catch (error) {
      console.error('Error loading ticket:', error);
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedTicket) return;
    try {
      setSaving(true);
      await updateTicket(selectedTicket.id, editFields);
      const updated = await getTicketById(selectedTicket.id);
      setSelectedTicket(updated);
      loadTickets();
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    try {
      setSending(true);
      await addTicketMessage(selectedTicket.id, replyText.trim(), 'support', 'BespokeBeauty Support');
      const updated = await getTicketById(selectedTicket.id);
      setSelectedTicket(updated);
      setEditFields({
        ticketStatus:   updated.ticketStatus,
        ticketSeverity: updated.ticketSeverity,
        ticketType:     updated.ticketType,
      });
      setReplyText('');
      setSelectedTemplate('');
      loadTickets();
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };

  // #Revised UI_v2 — SE5: apply template text to textarea
  const handleTemplateSelect = (e) => {
    const val = e.target.value;
    setSelectedTemplate(val);
    if (val) setReplyText(val);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString('en-SG', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getStatusClass = (status) => {
    if (status === 'Waiting for Support')  return 'waiting-support';
    if (status === 'Waiting for Customer') return 'waiting-customer';
    return 'closed';
  };

  const uniqueSalonIds = [...new Set(tickets.map(t => t.salonId))].sort();

  const filteredTickets = tickets.filter(ticket => {
    if (filterSalonId && ticket.salonId      !== filterSalonId) return false;
    if (filterStatus  && ticket.ticketStatus !== filterStatus)  return false;
    if (filterType    && ticket.ticketType   !== filterType)    return false;
    return true;
  });

  // ─── Detail view ──────────────────────────────────────────────
  if (selectedTicket) {
    const sevMeta = SEVERITY_META[selectedTicket.ticketSeverity] || { label: selectedTicket.ticketSeverity, cls: 'sev-low' };
    return (
      <div className="tickets-list-container">
        <div className="tickets-list-header">
          <div>
            <h2>Ticket Details</h2>
            <p className="tickets-list-subtitle">Ticket ID: {selectedTicket.id}</p>
          </div>
          <button className="ticket-back-btn" onClick={() => setSelectedTicket(null)}>
            ← Back to Tickets
          </button>
        </div>

        <div className="ticket-detail-container">
          <div className="ticket-detail-header">
            <h3>{selectedTicket.ticketHeader}</h3>
            <span className={`ticket-status-badge ${getStatusClass(selectedTicket.ticketStatus)}`}>
              {selectedTicket.ticketStatus}
            </span>
          </div>

          <div className="ticket-detail-fields">
            <div className="ticket-detail-field">
              <label>Salon ID</label>
              <span>{selectedTicket.salonId}</span>
            </div>
            <div className="ticket-detail-field">
              <label>Client Email</label>
              <span>{selectedTicket.clientEmail || '—'}</span>
            </div>
            <div className="ticket-detail-field">
              <label>Date Created</label>
              <span>{formatDate(selectedTicket.dateCreated)}</span>
            </div>
            <div className="ticket-detail-field">
              <label>Last Updated (Client)</label>
              <span>{formatDate(selectedTicket.dateUpdatedClient)}</span>
            </div>
            <div className="ticket-detail-field">
              <label>Last Updated (Support)</label>
              <span>{formatDate(selectedTicket.dateUpdatedSupport)}</span>
            </div>
            {/* #Revised UI_v2 — SE5: severity with colour badge */}
            <div className="ticket-detail-field">
              <label>Severity</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
                <span className={`ticket-severity-badge ${sevMeta.cls}`}>{sevMeta.label}</span>
                <select
                  value={editFields.ticketSeverity}
                  onChange={e => setEditFields(prev => ({ ...prev, ticketSeverity: e.target.value }))}
                >
                  {SEVERITY_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="ticket-detail-field">
              <label>Status</label>
              <select
                value={editFields.ticketStatus}
                onChange={e => setEditFields(prev => ({ ...prev, ticketStatus: e.target.value }))}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="ticket-detail-field">
              <label>Type</label>
              <select
                value={editFields.ticketType}
                onChange={e => setEditFields(prev => ({ ...prev, ticketType: e.target.value }))}
              >
                {TYPE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="ticket-save-changes-bar">
            <button
              className="ticket-save-btn"
              onClick={handleSaveChanges}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>

          <div className="ticket-detail-description">
            <h4>Description</h4>
            <p>{selectedTicket.ticketDescription}</p>
          </div>

          <div className="ticket-messages-section">
            <h4>Message Log</h4>
            <div className="ticket-messages">
              {(selectedTicket.messages || []).map((msg, idx) => (
                <div key={idx} className={`ticket-message ${msg.sender}`}>
                  <div className="ticket-message-sender">{msg.senderName}</div>
                  <div className="ticket-message-text">{msg.message}</div>
                  <div className="ticket-message-time">{formatDate(msg.timestamp)}</div>
                </div>
              ))}
            </div>

            {/* #Revised UI_v2 — SE5: quick-reply template dropdown */}
            <div className="sa-template-row">
              <label className="sa-template-label">Quick Reply:</label>
              <select
                className="sa-template-select"
                value={selectedTemplate}
                onChange={handleTemplateSelect}
              >
                {REPLY_TEMPLATES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="ticket-reply-area">
              <textarea
                value={replyText}
                onChange={e => { setReplyText(e.target.value); setSelectedTemplate(''); }}
                placeholder="Type your reply, or select a template above…"
              />
              <button
                className="ticket-reply-btn"
                onClick={handleSendReply}
                disabled={sending || !replyText.trim()}
              >
                {sending ? 'Sending…' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── List view ────────────────────────────────────────────────
  return (
    <div className="tickets-list-container">
      <div className="tickets-list-header">
        <div>
          <h2>Support Tickets</h2>
          <p className="tickets-list-subtitle">Manage support tickets from salon clients</p>
        </div>
      </div>

      <div className="ticket-filters-bar">
        <div className="ticket-filter-group">
          <label>Salon ID</label>
          <select value={filterSalonId} onChange={e => setFilterSalonId(e.target.value)}>
            <option value="">All Salons</option>
            {uniqueSalonIds.map(id => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
        </div>
        <div className="ticket-filter-group">
          <label>Status</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div className="ticket-filter-group">
          <label>Type</label>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            {TYPE_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="ticket-count">
        Showing {filteredTickets.length} of {tickets.length} tickets
      </div>

      {loading ? (
        <div className="ticket-loading">Loading tickets…</div>
      ) : filteredTickets.length === 0 ? (
        <div className="ticket-empty-state">
          <p>No tickets found.</p>
          {tickets.length > 0 && <p>Try adjusting your filters.</p>}
        </div>
      ) : (
        <table className="ticket-table">
          <thead>
            <tr>
              <th>Salon ID</th>
              <th>Ticket Header</th>
              <th>Date Created</th>
              <th>Updated (Client)</th>
              <th>Updated (Support)</th>
              {/* #Revised UI_v2 — SE5: severity + SLA columns */}
              <th>Severity</th>
              <th>SLA</th>
              <th>Type</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredTickets.map(ticket => {
              // #Revised UI_v2 — SE5: severity badge
              const sevMeta = SEVERITY_META[ticket.ticketSeverity] || { label: ticket.ticketSeverity, cls: 'sev-low' };
              // #Revised UI_v2 — SE5: SLA progress
              const sla = getSlaProgress(ticket);
              return (
                <tr key={ticket.id}>
                  <td>{ticket.salonId}</td>
                  <td>{ticket.ticketHeader}</td>
                  <td>{formatDate(ticket.dateCreated)}</td>
                  <td>{formatDate(ticket.dateUpdatedClient)}</td>
                  <td>{formatDate(ticket.dateUpdatedSupport)}</td>
                  <td>
                    <span className={`ticket-severity-badge ${sevMeta.cls}`}>
                      {sevMeta.label}
                    </span>
                  </td>
                  {/* #Revised UI_v2 — SE5: SLA progress bar */}
                  <td className="sa-sla-cell">
                    {sla ? (
                      <>
                        <div className="sa-sla-bar-outer">
                          <div
                            className="sa-sla-bar-fill"
                            style={{ width: `${sla.pct}%`, background: sla.color }}
                          />
                        </div>
                        <span className="sa-sla-label" style={{ color: sla.color }}>
                          {sla.label}
                        </span>
                      </>
                    ) : (
                      <span style={{ color: '#aaa', fontSize: 12 }}>Closed</span>
                    )}
                  </td>
                  <td>{ticket.ticketType}</td>
                  <td>
                    <span className={`ticket-status-badge ${getStatusClass(ticket.ticketStatus)}`}>
                      {ticket.ticketStatus}
                    </span>
                  </td>
                  <td>
                    <button
                      className="ticket-view-btn"
                      onClick={() => handleViewTicket(ticket.id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default TicketsList;
